import * as Dialog from "@radix-ui/react-dialog";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { describeDaysLeft } from "@logiful/domain";
import { CalendarClock, CalendarX2, CheckCircle2, Search, Settings2, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { Pager } from "@/components/Pager";
import { Link } from "react-router";
import { toast } from "sonner";
import { useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton, Tip } from "@/components/ui";
import { api, ApiError, canManage, type BestBeforeLevel, type BestBeforeLot } from "@/lib/api";
import { fmtNumber } from "@/lib/format";
import { intlLocale, t, msg } from "@/lib/i18n";

export const LEVEL: Record<BestBeforeLevel, { label: string; text: string; bg: string; dot: string }> = {
  expired: { label: msg("Abgelaufen"), text: msg("text-danger"), bg: "bg-danger-soft border-danger/25", dot: "bg-danger" },
  critical: { label: msg("Kritisch"), text: msg("text-danger"), bg: "bg-danger-soft/60 border-danger/20", dot: "bg-danger/70" },
  warning: { label: msg("Warnung"), text: msg("text-warning"), bg: "bg-warning-soft border-warning/25", dot: "bg-warning" },
  ok: { label: msg("OK"), text: msg("text-success"), bg: "bg-success-soft border-success/20", dot: "bg-success" },
};

const ICON: Record<BestBeforeLevel, typeof CalendarX2> = { expired: CalendarX2, critical: TriangleAlert, warning: CalendarClock, ok: CheckCircle2 };

export function BestBeforePanel() {
  const { tenantId, role } = useTenant();
  const [filter, setFilter] = useState<BestBeforeLevel | "attention">("attention");
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  useEffect(() => { const id = setTimeout(() => setTerm(search.trim()), 250); return () => clearTimeout(id); }, [search]);
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [filter, term]);
  // Einstufung, Zähler und Seiten im Server – auch bei Tausenden Chargen schnell
  const q = useQuery({ queryKey: ["best-before", tenantId, "page", filter, term, page], queryFn: () => api.stock.bestBeforePage(tenantId, { page, pageSize: 100, level: filter, ...(term ? { search: term } : {}) }), placeholderData: keepPreviousData });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const d = q.data;
  const lots = d?.lots ?? [];

  if (q.isLoading || !d) return <Skeleton className="h-64 w-full" />;
  const attention = d.summary.expired.lots + d.summary.critical.lots + d.summary.warning.lots;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {(["expired", "critical", "warning", "ok"] as const).map((lv) => {
          const Icon = ICON[lv];
          const s = d.summary[lv];
          const active = filter === lv;
          return (
            <button key={lv} onClick={() => setFilter(active ? "attention" : lv)} aria-pressed={active} className={cx("text-left rounded-xl border p-4 transition-shadow hover:shadow-md", active ? "border-ink ring-1 ring-ink" : "border-border bg-surface")}>
              <div className={cx("flex items-center gap-2 text-[12.5px] font-medium", LEVEL[lv].text)}><Icon className="size-4" /> {t(LEVEL[lv].label)}</div>
              <div className="mt-2 text-[26px] font-semibold tabular-nums">{fmtNumber(s.lots)} <span className="text-[13px] font-normal text-fg-muted">{s.lots === 1 ? t("Charge") : t("Chargen")}</span></div>
              <div className="text-[12px] text-fg-subtle">{fmtNumber(s.units)} {t("Einheiten")}</div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface px-4 py-2.5 text-[13px]">
        <Settings2 className="size-4 text-fg-subtle" />
        <span>{t("Warnung ab")} <b className="tabular-nums">{d.settings.warningDays}</b> {t("Tagen Restlaufzeit · kritisch ab")} <b className="tabular-nums">{d.settings.criticalDays}</b> {t("Tagen")}</span>
        <span className="text-fg-subtle">{t("· einzelne Artikel können abweichen")}</span>
        {canManage(role) && <Button size="sm" className="ml-auto" onClick={() => setSettingsOpen(true)}>{t("Anpassen")}</Button>}
      </div>

      <Card className="overflow-hidden">
        <div className="flex items-center gap-2 px-4 h-11 border-b border-border text-[12.5px]">
          <span className="font-medium">{filter === "attention" ? t("Handlungsbedarf ({attention})", { attention: fmtNumber(attention) }) : `${LEVEL[filter].label} (${fmtNumber(d.summary[filter].lots)})`}</span>
          {filter !== "attention" && <button onClick={() => setFilter("attention")} className="text-fg-muted hover:text-fg underline underline-offset-2">{t("alle mit Handlungsbedarf")}</button>}
          <div className="ml-auto w-56"><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("SKU, Artikel, Charge …")} leading={<Search className="size-3.5" />} aria-label={t("Chargen durchsuchen")} /></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="text-[12px] text-fg-subtle bg-surface-2/50 border-b border-border">
              <tr>
                <th className="h-9 pl-4 pr-3 font-medium">{t("Artikel")}</th>
                <th className="h-9 px-3 font-medium">{t("Charge")}</th>
                <th className="h-9 px-3 font-medium">{t("MHD")}</th>
                <th className="h-9 px-3 font-medium">{t("Restlaufzeit")}</th>
                <th className="h-9 px-3 font-medium text-right">{t("Menge")}</th>
                <th className="h-9 pl-3 pr-4 font-medium">{t("Schwellen")}</th>
              </tr>
            </thead>
            <tbody>{lots.map((l) => <LotRow key={l.lotId} l={l} />)}</tbody>
          </table>
        </div>
        <Pager total={d.total} page={page} pageSize={d.pageSize} onPage={setPage} />
        {lots.length === 0 && (
          <EmptyState icon={<CheckCircle2 className="size-5" />} title={term ? t("Nichts gefunden für „{term}“", { term }) : filter === "attention" ? t("Alles im grünen Bereich") : t("Keine Chargen in dieser Stufe")}>
            {!term && filter === "attention" ? t("Keine Charge läuft in den nächsten {warningDays} Tagen ab.", { warningDays: d.settings.warningDays }) : undefined}
          </EmptyState>
        )}
      </Card>
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} current={d.settings} />
    </div>
  );
}

function LotRow({ l }: { l: BestBeforeLot }) {
  const meta = LEVEL[l.level];
  // Balken: Anteil der Restlaufzeit bis zur Warnschwelle (voll = mindestens Warnschwelle)
  const pct = Math.max(0, Math.min(100, (l.daysLeft / l.thresholds.warningDays) * 100));
  return (
    <tr className="border-b border-border last:border-0 hover:bg-surface-2/50">
      <td className="pl-4 pr-3 py-2.5">
        <Link to={`/products?open=${l.productId}`} className="font-medium hover:underline">{l.name}</Link>
        <div className="font-mono text-[11.5px] text-fg-muted">{l.sku}</div>
      </td>
      <td className="px-3 py-2.5 font-mono text-[12.5px]">{l.batch ?? "—"}</td>
      <td className="px-3 py-2.5 tabular-nums text-[13px]">{new Date(l.bestBefore).toLocaleDateString(intlLocale())}</td>
      <td className="px-3 py-2.5 min-w-[200px]">
        <span className={cx("inline-flex items-center gap-1.5 h-6 px-2 rounded-md border text-[12px] font-medium", meta.bg, meta.text)}>
          <span className={cx("size-1.5 rounded-full", meta.dot)} /> {describeDaysLeft(l.daysLeft)}
        </span>
        {l.level !== "expired" && (
          <div className="mt-1.5 h-1 w-32 rounded-full bg-surface-2 overflow-hidden"><div className={cx("h-full rounded-full", meta.dot)} style={{ width: `${pct}%` }} /></div>
        )}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums">
        {fmtNumber(l.quantity)}
        {l.blocked > 0 && <Tip content={t("Im Lager gesperrt – wird nicht mehr versendet")}><span className="block text-[11.5px] text-warning">{fmtNumber(l.blocked)} {t("gesperrt")}</span></Tip>}
      </td>
      <td className="pl-3 pr-4 py-2.5 text-[12px] text-fg-muted whitespace-nowrap">
        {l.thresholds.warningDays} / {l.thresholds.criticalDays} T. {l.customThresholds && <Badge tone="info" className="ml-1">{t("Artikel")}</Badge>}
      </td>
    </tr>
  );
}

function SettingsDialog({ open, onOpenChange, current }: { open: boolean; onOpenChange: (o: boolean) => void; current: { warningDays: number; criticalDays: number } }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const [w, setW] = useState(String(current.warningDays));
  const [c, setC] = useState(String(current.criticalDays));
  const wn = Number(w), cn = Number(c);
  const valid = Number.isInteger(wn) && Number.isInteger(cn) && wn >= 1 && cn >= 0 && cn < wn;

  // Live-Vorschau: so würden die heutigen Chargen mit den neuen Werten eingestuft (Artikel-Overrides bleiben) – im Server gezählt
  const [debounced, setDebounced] = useState({ wn, cn });
  useEffect(() => { const id = setTimeout(() => setDebounced({ wn, cn }), 300); return () => clearTimeout(id); }, [wn, cn]);
  const pv = useQuery({ queryKey: ["best-before", tenantId, "preview", debounced.wn, debounced.cn], queryFn: () => api.stock.bestBeforePreview(tenantId, debounced.wn, debounced.cn), enabled: open && valid, placeholderData: keepPreviousData });
  const preview = valid && pv.data ? Object.fromEntries(Object.entries(pv.data.summary).map(([k, v]) => [k, v.lots])) as Record<BestBeforeLevel, number> : null;

  const save = useMutation({
    mutationFn: () => api.stock.saveBestBeforeSettings(tenantId, { warningDays: wn, criticalDays: cn }),
    onSuccess: () => {
      toast.success("MHD-Schwellen gespeichert", { description: `Warnung ab ${wn} Tagen, kritisch ab ${cn} Tagen` });
      void qc.invalidateQueries({ queryKey: ["best-before", tenantId] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Speichern fehlgeschlagen") : "Speichern fehlgeschlagen"),
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(460px,calc(100vw-32px))] rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
          <Dialog.Title className="text-[17px] font-semibold">{t("MHD-Warnungen einstellen")}</Dialog.Title>
          <Dialog.Description className="text-fg-muted mt-1">{t("Gilt für alle Artikel – außer für Artikel mit eigenen Schwellen (im Artikel einstellbar).")}</Dialog.Description>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[12px] font-medium text-fg-muted flex items-center gap-1.5"><span className="size-2 rounded-full bg-warning" /> {t("Warnung ab")}</span>
              <Input type="number" min={1} value={w} onChange={(e) => setW(e.target.value)} suffix={t("Tagen")} className="mt-1" invalid={!valid} />
            </label>
            <label className="block">
              <span className="text-[12px] font-medium text-fg-muted flex items-center gap-1.5"><span className="size-2 rounded-full bg-danger" /> {t("Kritisch ab")}</span>
              <Input type="number" min={0} value={c} onChange={(e) => setC(e.target.value)} suffix={t("Tagen")} className="mt-1" invalid={!valid} />
            </label>
          </div>
          {!valid && <p className="mt-2 text-[12px] text-danger">{t("„Kritisch“ muss kleiner als „Warnung“ sein.")}</p>}
          {preview && (
            <div className="mt-4 rounded-xl bg-surface-2/60 border border-border p-3">
              <p className="text-[12px] text-fg-muted mb-2">{t("Vorschau mit heutigen Chargen")}</p>
              <div className="grid grid-cols-4 gap-2 text-center">
                {(["expired", "critical", "warning", "ok"] as const).map((lv) => (
                  <div key={lv}><div className={cx("text-[18px] font-semibold tabular-nums", LEVEL[lv].text)}>{preview[lv]}</div><div className="text-[11px] text-fg-subtle">{t(LEVEL[lv].label)}</div></div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
            <Button variant="primary" disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
